import { isEmpty, ltrimString } from "./Validator";
import {
  AUTH_SYNC_LOCAL_STORAGE_KEY,
  EXPLICIT_LOGOUT_LOCAL_STORAGE_KEY,
  OPPORTUNITY_CODE_LOCAL_STORAGE_KEY,
  SESSION_CONTINUE_EVENT,
  SESSION_CONTINUE_LOCAL_STORAGE_KEY,
  SESSION_LOGOUT_COMPLETE_LOCAL_STORAGE_KEY,
  SESSION_LOGOUT_LOCAL_STORAGE_KEY,
  TOKEN_REFRESH_LOCK_LOCAL_STORAGE_KEY,
  TOKEN_REFRESH_LOCK_NAME,
  TOKEN_REFRESH_LOCK_TIMEOUT,
  TOKEN_REFRESH_SYNC_LOCAL_STORAGE_KEY,
} from "../Constant";

export function notifyLoginAcrossTabs() {
  localStorage.setItem(AUTH_SYNC_LOCAL_STORAGE_KEY, String(Date.now()));
}

export function removeLoginSyncFromLocal() {
  localStorage.removeItem(AUTH_SYNC_LOCAL_STORAGE_KEY);
}

export function markExplicitLogout() {
  localStorage.setItem(EXPLICIT_LOGOUT_LOCAL_STORAGE_KEY, "true");
}

export function clearExplicitLogout() {
  localStorage.removeItem(EXPLICIT_LOGOUT_LOCAL_STORAGE_KEY);
}

export function hasExplicitLogout() {
  return (
    localStorage.getItem(EXPLICIT_LOGOUT_LOCAL_STORAGE_KEY) === "true"
  );
}

export function notifySessionContinueAcrossTabs() {
  localStorage.setItem(SESSION_CONTINUE_LOCAL_STORAGE_KEY, String(Date.now()));
  window.dispatchEvent(new Event(SESSION_CONTINUE_EVENT));
}

export function notifySessionLogoutAcrossTabs() {
  localStorage.setItem(SESSION_LOGOUT_LOCAL_STORAGE_KEY, String(Date.now()));
}

export function notifySessionLogoutCompleteAcrossTabs(success: boolean) {
  localStorage.setItem(
    SESSION_LOGOUT_COMPLETE_LOCAL_STORAGE_KEY,
    `${Date.now()}:${success ? "success" : "failed"}`,
  );
}

export function notifyTokenRefreshAcrossTabs() {
  localStorage.setItem(TOKEN_REFRESH_SYNC_LOCAL_STORAGE_KEY, String(Date.now()));
}

export function clearTokenMismatch() {
  localStorage.removeItem("isTokenMismatch");
}

export function withTokenRefreshLock<T>(callback: () => Promise<T>) {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request(TOKEN_REFRESH_LOCK_NAME, callback);
  }

  const lockOwner = `${Date.now()}-${Math.random()}`;
  const waitForLock = () =>
    new Promise<void>((resolve) => setTimeout(resolve, 100));

  const acquireLock = async (): Promise<T> => {
    while (true) {
      const now = Date.now();
      const currentLock = localStorage.getItem(
        TOKEN_REFRESH_LOCK_LOCAL_STORAGE_KEY,
      );
      const lockExpiry = Number(currentLock?.split(":").pop() || 0);

      if (currentLock && lockExpiry > now) {
        await waitForLock();
        continue;
      }

      const lockValue = `${lockOwner}:${now + TOKEN_REFRESH_LOCK_TIMEOUT}`;
      localStorage.setItem(TOKEN_REFRESH_LOCK_LOCAL_STORAGE_KEY, lockValue);
      await waitForLock();

      if (
        localStorage.getItem(TOKEN_REFRESH_LOCK_LOCAL_STORAGE_KEY) !==
        lockValue
      ) {
        continue;
      }

      try {
        return await callback();
      } finally {
        if (
          localStorage.getItem(TOKEN_REFRESH_LOCK_LOCAL_STORAGE_KEY) ===
          lockValue
        ) {
          localStorage.removeItem(TOKEN_REFRESH_LOCK_LOCAL_STORAGE_KEY);
        }
      }
    }
  };

  return acquireLock();
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function debounce(
  func: { apply: (arg0: any, arg1: any[]) => void },
  timeout = 300,
) {
  let timer: string | number | NodeJS.Timeout | undefined;
  return function (this: any, ...args: any[]) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

export function padNumber(num: { toString: () => string }, length: any) {
  return num.toString().padStart(length, "0");
}

export function sortOptions(options: { label: string; value: string }[]) {
  if (!Array.isArray(options) || options.length === 0) {
    return []; // Return an empty array if options is not an array or is empty
  }
  const selectOption = options.filter((option) => {
    if (option.label.toLowerCase().includes("select")) {
      return true;
    }
  });
  const remainingOptions = options.filter((option) => {
    if (!option.label.toLowerCase().includes("select")) {
      return true;
    }
  });
  const sortOptions = remainingOptions.sort(function (a, b) {
    return a.label.localeCompare(b.label);
  });
  return selectOption.length > 0
    ? [...selectOption, ...sortOptions]
    : sortOptions;
}

export const isTokenExpired = (token: string | null) => {
  if (!token) return false; // If no token, consider it expired
  return Date.now() >= JSON.parse(atob(token.split(".")[1])).exp * 1000;
};

export function truncateString(str: string, maxLength: number) {
  if (str.length > maxLength) {
    // Subtract 3 from maxLength to account for the '...'
    return str.slice(0, maxLength - 3) + "...";
  }
  return str;
}

export const crypt = (salt: string, text: string) => {
  const textToChars = (text: string) =>
    text.split("").map((c) => c.charCodeAt(0));
  const byteHex = (n: string) => ("0" + Number(n).toString(16)).substr(-2);
  const applySaltToChar = (code: any) =>
    textToChars(salt).reduce((a, b) => a ^ b, code);
  return text
    .split("")
    .map(textToChars)
    .map(applySaltToChar)
    .map(byteHex)
    .join("");
};

export const decrypt = (salt: string, encoded: string) => {
  const textToChars = (text: string) =>
    text.split("").map((c) => c.charCodeAt(0));
  const applySaltToChar = (code: any) =>
    textToChars(salt).reduce((a, b) => a ^ b, code);
  const matches = encoded.match(/.{1,2}/g);
  if (!matches) return ""; // Return an empty string if no matches are found
  return matches
    .map((hex) => parseInt(hex, 16))
    .map(applySaltToChar)
    .map((charCode) => String.fromCharCode(charCode))
    .join("");
};

export function setAccessTokenInLocal(token: string | null) {
  if (!token) {
    localStorage.removeItem("accessToken");
    return;
  }
  const tokenEncrypted = crypt("salt", token); // -> 426f666665
  localStorage.setItem("accessToken", tokenEncrypted);
}

export function getAccessTokenFromLocal() {
  const tokenEncrypted = localStorage.getItem("accessToken");
  return decrypt("salt", tokenEncrypted || "");
}
export function removeAccessTokenFromLocal() {
  localStorage.removeItem("accessToken");
}

export function setOrderId(orederId: string | null) {
  if (!orederId) {
    localStorage.removeItem("orderId");
    return;
  }
  localStorage.setItem("orderId", orederId);
}

export function getOrderIdFromLocal() {
  const currentOrderID = localStorage.getItem("orderId") || 0;
  return Number(currentOrderID);
}

export function removeOrderIdFromLocal() {
  localStorage.removeItem("orderId");
}

export function setOpportunityCodeInLocal(opportunityCode: string | null) {
  const normalizedOpportunityCode = (opportunityCode || "")
    .trim()
    .toUpperCase();
  const existingOpportunityCode =
    localStorage
      .getItem(OPPORTUNITY_CODE_LOCAL_STORAGE_KEY)
      ?.trim()
      .toUpperCase() || "";

  if (!normalizedOpportunityCode) {
    if (existingOpportunityCode) {
      localStorage.removeItem(OPPORTUNITY_CODE_LOCAL_STORAGE_KEY);
    }
    return;
  }

  if (existingOpportunityCode === normalizedOpportunityCode) {
    return;
  }

  localStorage.setItem(
    OPPORTUNITY_CODE_LOCAL_STORAGE_KEY,
    normalizedOpportunityCode,
  );
}

export function getOpportunityCodeFromLocal() {
  return (
    localStorage
      .getItem(OPPORTUNITY_CODE_LOCAL_STORAGE_KEY)
      ?.trim()
      .toUpperCase() || ""
  );
}

export function removeOpportunityCodeFromLocal() {
  localStorage.removeItem(OPPORTUNITY_CODE_LOCAL_STORAGE_KEY);
}

// this function handle input type number remove char and allow only 1 digit after decimal
export const handleNumber = (value: string) => {
  let val = value;
  val = ltrimString(value);
  val = val.replace(/[^0-9.]/g, "");
  val = val.replace(/(\..*)\./g, "$1");
  if (val && val.includes(".")) {
    const [intPart, decPart] = val.split(".");
    val = intPart + "." + decPart.slice(0, 1);
  }
  return val;
};

// this function adds 0 prefix if value starts with . (e.g., .7 becomes 0.7)
export const formatNumberWithZeroPrefix = (value: string) => {
  let val = value.trim();
  if (val.startsWith(".")) {
    val = "0" + val;
  }
  return val;
};

export function maskSkuKey(key: string, showFull = false) {
  if (!key) return key;

  if (showFull) {
    return key;
  }

  if (key.length <= 4) return key;

  const visiblePart = key.slice(-4);
  const maskedPart = "*".repeat(key.length - 4);
  return maskedPart + visiblePart;
}

export const formatUSDWithCommas = (val: any) => {
  val = String(val ?? "");
  const [intPart, decPart] = val.split(".");
  const intWithCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${intWithCommas}.${decPart}` : intWithCommas;
};

export const containsEmoji = (text: string): boolean => {
  const emojiRegex = /\p{Extended_Pictographic}/gu;
  return emojiRegex.test(text);
};

export const removeEmojis = (text: string): string => {
  const emojiRegex = /\p{Extended_Pictographic}/gu;
  return text.replace(emojiRegex, "");
};

export function TextAbstract(text: string, length: number) {
  if (text == null) {
    return "";
  }
  if (text.length <= length) {
    return text;
  }
  text = text.substring(0, length);
  let last = text.lastIndexOf("");
  text = text.substring(0, last);
  return text + "...";
}

export function downloadBase64File(
  contentBase64: string,
  fileName: string,
  contentType = "application/octet-stream",
) {
  const linkSource = `data:${contentType};base64,${contentBase64}`;
  const downloadLink = document.createElement("a");
  document.body.appendChild(downloadLink);

  downloadLink.href = linkSource;
  downloadLink.target = "_self";
  downloadLink.download = fileName;
  downloadLink.click();
}

export const isDateDifferenceLessThenTwo = (date1: Date | null) => {
  if (date1 === null) {
    return true;
  }
  var date2 = new Date();
  if (date2 > date1) return true;
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.abs((date1.getTime() - date2.getTime()) / oneDay) < 2;
};

export function formattingBytes(x: string) {
  const units = ["bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  let l = 0,
    n = parseInt(x, 10) || 0;
  while (n >= 1024 && ++l) {
    n = n / 1024;
  }
  return n.toFixed(n < 10 && l > 0 ? 1 : 0) + " " + units[l];
}

export function urlify(text: string) {
  let urlRegex = new RegExp(
    "([a-zA-Z0-9]+://)?([a-zA-Z0-9_]+:[a-zA-Z0-9_]+@)?([a-zA-Z0-9.-]+\\.[A-Za-z]{2,4})(:[0-9]+)?(/.*)?",
    "g",
  );
  return text.replace(urlRegex, (url) => {
    if (url.search(/^http[s]?\:\/\//) === -1) {
      return "https://" + url;
    } else {
      return url;
    }
  });
}

export const isSkuFixedOrOneTimeCost = (
  costMetricCd: string,
  costIntervalCd: string,
) => {
  const isFixedCostArr = costMetricCd.split("_");
  return isFixedCostArr[0] === "fix" || costIntervalCd === "onetime";
};

export const getArtifactFileName = (artifactUrl?: string | null) => {
  if (!artifactUrl) {
    return "-";
  }
  const normalizedUrl = artifactUrl.split("?")[0];
  const fileName = normalizedUrl.split("/").pop();
  return fileName ? decodeURIComponent(fileName) : "-";
};

export function isAbortError(error: unknown): error is DOMException {
  return error instanceof DOMException && error.name === "AbortError";
}

export function sanitizeHTMLSpace(input: string): string {
  return input ? input.replace(/&nbsp;/g, " ") : "";
}

export const formatMetricByQuantity = (
  quantity: number,
  metric: string,
): string => {
  if (isEmpty(quantity) || isEmpty(metric)) {
    return "--";
  }
  if (quantity > 1) {
    return quantity + " " + metric;
  }
  return quantity + " " + metric.slice(0, -1);
};

export const formatValueInMoneyFormat = (value: number) => {
  if (Number.isNaN(value)) {
    return "0";
  }
  const moneyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });
  return moneyFormatter.format(value);
};

export const isDatePassed = (publishDate: Date | null) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return publishDate !== null && publishDate < today;
};
